import type { Bonus } from '../types/domain'

export interface FreeBetGroup {
  /** Stable identity for the group (amount · currency · games · expiry-day). */
  key: string
  amount: number
  currency: string
  /** Empty array = applicable to all games (platform convention). */
  applicableGames: string[]
  /** Earliest expiry among the grouped bets, or null when none expire. */
  expiresAt: string | null
  count: number
  items: Bonus[]
}

const gamesSignature = (applicableGames: string[]): string =>
  applicableGames.length === 0 ? 'ALL' : [...applicableGames].sort().join(',')

// Bucket by calendar day (UTC) so a single grant — which shares one expiry —
// stays together, while genuinely different expiries split.
const expiryBucket = (expiresAt: string | null | undefined): string =>
  expiresAt ? expiresAt.slice(0, 10) : 'none'

const groupKey = (item: Bonus): string =>
  `${item.amount}|${item.currency}|${gamesSignature(item.applicableGames)}|${expiryBucket(item.expiresAt)}`

const earliest = (a: string | null, b: string | null | undefined): string | null => {
  if (!b) return a
  if (!a) return b
  return b < a ? b : a
}

/**
 * Groups free bets that are interchangeable from the player's point of view —
 * same value, currency, game-applicability and expiry day — so the UI can show
 * "10 × $5" instead of ten identical entries. Groups appear in first-seen order;
 * items within a group keep their input order.
 */
export function groupFreeBets(freeBets: Bonus[]): FreeBetGroup[] {
  const byKey = new Map<string, FreeBetGroup>()

  for (const item of freeBets) {
    const key = groupKey(item)
    const existing = byKey.get(key)

    if (existing) {
      existing.items.push(item)
      existing.count += 1
      existing.expiresAt = earliest(existing.expiresAt, item.expiresAt)
    } else {
      byKey.set(key, {
        key,
        amount: item.amount,
        currency: item.currency,
        applicableGames: item.applicableGames,
        expiresAt: item.expiresAt ?? null,
        count: 1,
        items: [item]
      })
    }
  }

  return Array.from(byKey.values())
}
